import React, { useCallback } from 'react';
import { Box, Button } from '@mui/material';

import { themeV4 } from '../../../../../core/theme.js';

export function MuiOptionButtons({
  options,
  isQuickQuestion = false,
  displayMode = 'chat',
  onSelect,
}) {
  const theme = themeV4;
  const safeOptions = Array.isArray(options) ? options : [];
  const isDashletQuestion = displayMode === 'dashlet';
  const isDashletCategory = displayMode === 'dashletCategories';
  const isDashlet = isDashletQuestion || isDashletCategory;
  const buttonSx = isDashletCategory ? {
    width: 'auto',
    minWidth: {xs: '100%', sm: 210},
    minHeight: 38,
    justifyContent: 'center',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: '19px',
    backgroundColor: '#fff',
    color: theme.palette.primary.main,
    px: 2,
    py: 0.75,
    fontSize: 13,
    lineHeight: 1.25,
    fontWeight: 700,
    textAlign: 'center',
    textTransform: 'none',
    whiteSpace: 'normal',
    transition: 'background-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
    '& .MuiButton-startIcon': {
      display: 'inline-flex !important',
      alignItems: 'center',
      marginLeft: '0 !important',
      marginRight: '8px !important',
      color: '#fff !important',
      opacity: '1 !important',
      visibility: 'visible !important',
    },
    '& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-startIcon svg': {
      display: 'block !important',
      width: '18px !important',
      height: '18px !important',
      color: '#fff !important',
      fill: '#fff !important',
      stroke: '#fff !important',
      opacity: '1 !important',
      visibility: 'visible !important',
    },
    '& .MuiButton-startIcon svg path': {
      fill: '#fff !important',
      color: '#fff !important',
    },
    '&:hover, &:focus-visible': {
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: '#eef6f2',
      boxShadow: '0 2px 8px rgba(35, 95, 77, .14)',
      outline: 'none',
      transform: 'translateY(-1px)',
    },
  } : (isDashletQuestion ? {
    width: '100%',
    justifyContent: 'flex-start',
    border: `1px solid ${theme.palette.primary.main} !important`,
    borderRadius: '16px',
    backgroundColor: '#fff !important',
    color: `${theme.palette.primary.main} !important`,
    boxShadow: 'none !important',
    px: 1.5,
    py: 0.9,
    fontSize: 13,
    lineHeight: 1.35,
    fontWeight: 600,
    textAlign: 'left',
    textTransform: 'none',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    transition: 'background-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
    '&:hover, &:focus-visible': {
      border: `1px solid ${theme.palette.primary.main} !important`,
      backgroundColor: '#eef6f2 !important',
      color: `${theme.palette.primary.main} !important`,
      boxShadow: '0 2px 8px rgba(35, 95, 77, .12)',
      outline: 'none',
      transform: 'translateY(-1px)',
    },
  } : (isQuickQuestion ? {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    textTransform: 'none !important',
    borderRadius: '16px !important',
    borderWidth: '2px',
    borderStyle: 'solid',
    height: 'auto',
    minHeight: 36,
    py: 0.75,
    whiteSpace: 'normal',
    lineHeight: 1.3,
    textAlign: 'center',
    overflowWrap: 'anywhere',
    '&:hover': {
      backgroundColor: '#fff',
      borderWidth: '2px',
    },
  } : {}));

  return (
    <Box
      component={isDashlet ? 'ul' : 'div'}
      sx={{
        flex: '1 1 auto',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        px: isDashlet ? 0 : '5px',
        m: 0,
        p: isDashlet ? 0 : undefined,
        listStyle: isDashlet ? 'none' : undefined,
        display: 'flex',
        justifyContent: isDashletCategory ? 'center' : 'flex-start',
        flexDirection: isDashletQuestion || !isQuickQuestion && !isDashletCategory ? 'column' : 'row',
        flexWrap: isDashletCategory || isQuickQuestion && !isDashlet ? 'wrap' : 'nowrap',
        gap: isDashletQuestion ? 1 : (isDashletCategory || isQuickQuestion ? 1.25 : 0),
        '& > *': {
          flex: '0 0 auto',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          ml: '0 !important',
        },
        '& > * + *': isQuickQuestion || isDashlet ? {} : {mt: 2},
      }}
      className={`qeeChatButtonContainer ${isQuickQuestion ? 'qeeQuickQuestionContainer' : ''}`}
    >
      {safeOptions.map((option, index) => {
        const button = (
          <Button
            key={isDashlet ? undefined : (option.value ?? index)}
            type="button"
            value={option.value}
            onClick={() => typeof onSelect === 'function' && onSelect(option)}
            variant={isDashlet ? 'text' : (isQuickQuestion ? 'outlined' : 'contained')}
            color="primary"
            sx={[
              buttonSx,
              isDashletCategory && option.selected ? {
                backgroundColor: '#eef6f2',
                boxShadow: 'inset 0 0 0 1px rgba(35, 95, 77, .12)',
              } : null,
            ]}
            startIcon={option.icon || undefined}
            {...(option.buttonProps || {})}
            className={`qeeChatButton ${isQuickQuestion ? 'qeeQuickQuestion' : ''}`}
          >
            {option.text}
          </Button>
        );
        return isDashlet
          ? <Box component="li" key={option.value ?? index}>{button}</Box>
          : button;
      })}
    </Box>
  );
}

export function MuiSelectInput({
  chatController,
  actionRequest,
  isQuickQuestion = false, // Accept an isQuickQuestion prop
}) {
  const setResponse = useCallback((option) => {
    const res = {
      type: 'select',
	  value: option.text,
	  prompt: option.prompt || option.text,
      answerType: option.answerType,
      answer: option.answer,
      quickQuestions: option.quickQuestions,
	  followUpContext: option.followUpContext,
      option
    };
    chatController.setActionResponse(actionRequest, res);
  }, [actionRequest, chatController]);

  return (
    <MuiOptionButtons
      options={actionRequest.options}
      isQuickQuestion={isQuickQuestion}
      onSelect={setResponse}
    />
  );
}
